import { useForm } from "react-hook-form";
import { Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { toast } from "sonner";

import { useMutation } from "@tanstack/react-query";
import { Background } from "@/components/Background";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import talyko from "@/assets/TalykoLogo.png";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { createCompany } from "@/api/company/create-company";
import { useNavigate } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const createCompanySchema = z.object({
  companyName: z.string({ message: "Company name is required" }),
  companyAddress: z.string({ message: "Company Address is required" }),
  companyEmail: z.string().email({ message: "Must be a valid email address" }),
  companyState: z.string(),
  companyCity: z.string(),
  companyZip: z.string(),
});

type CreateCompanyType = z.infer<typeof createCompanySchema>;

export function RegisterCompany() {
  const navigate = useNavigate();

  const { control, register, handleSubmit, reset } = useForm<CreateCompanyType>(
    {
      resolver: zodResolver(createCompanySchema),
    },
  );

  const { mutateAsync: CreateCompanyFn } = useMutation({
    mutationKey: ["createCompany"],
    mutationFn: createCompany,
  });

  // const session = User?.user;

  async function handleCreateCompany(data: CreateCompanyType) {
    try {
      const { status } = await CreateCompanyFn({
        companyName: data.companyName,
        companyAddress: data.companyAddress,
        companyEmail: data.companyEmail,
        companyState: data.companyState,
        companyCity: data.companyCity,
        companyZip: data.companyZip,
      });
      if (status === 201) {
        toast.success("Company created successfully");
        reset();
        navigate("/dashboard");
      }
    } catch (err) {
      console.log(err);
      toast.error("Error creating company");
    }
  }

  const states = [
    { value: "AL", label: "Alabama" },
    { value: "AK", label: "Alaska" },
    { value: "AZ", label: "Arizona" },
    { value: "AR", label: "Arkansas" },
    { value: "CA", label: "California" },
    { value: "CO", label: "Colorado" },
    { value: "CT", label: "Connecticut" },
    { value: "DE", label: "Delaware" },
    { value: "FL", label: "Florida" },
    { value: "GA", label: "Georgia" },
    { value: "HI", label: "Hawaii" },
    { value: "ID", label: "Idaho" },
    { value: "IL", label: "Illinois" },
    { value: "IN", label: "Indiana" },
    { value: "IA", label: "Iowa" },
    { value: "KS", label: "Kansas" },
    { value: "KY", label: "Kentucky" },
    { value: "LA", label: "Louisiana" },
    { value: "ME", label: "Maine" },
    { value: "MD", label: "Maryland" },
    { value: "MA", label: "Massachusetts" },
    { value: "MI", label: "Michigan" },
    { value: "MN", label: "Minnesota" },
    { value: "MS", label: "Mississippi" },
    { value: "MO", label: "Missouri" },
    { value: "MT", label: "Montana" },
    { value: "NE", label: "Nebraska" },
    { value: "NV", label: "Nevada" },
    { value: "NH", label: "New Hampshire" },
    { value: "NJ", label: "New Jersey" },
    { value: "NM", label: "New Mexico" },
    { value: "NY", label: "New York" },
    { value: "NC", label: "North Carolina" },
    { value: "ND", label: "North Dakota" },
    { value: "OH", label: "Ohio" },
    { value: "OK", label: "Oklahoma" },
    { value: "OR", label: "Oregon" },
    { value: "PA", label: "Pennsylvania" },
    { value: "RI", label: "Rhode Island" },
    { value: "SC", label: "South Carolina" },
    { value: "SD", label: "South Dakota" },
    { value: "TN", label: "Tennessee" },
    { value: "TX", label: "Texas" },
    { value: "UT", label: "Utah" },
    { value: "VT", label: "Vermont" },
    { value: "VA", label: "Virginia" },
    { value: "WA", label: "Washington" },
    { value: "WV", label: "West Virginia" },
    { value: "WI", label: "Wisconsin" },
    { value: "WY", label: "Wyoming" },
  ];

  return (
    <div className="relative">
      <Background>
        {/* First quadrant with SVG curve */}
        <div className="relative h-full w-full">
          <div className="absolute hidden h-full w-full sm:block">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 800 800"
              className="h-full w-full"
              preserveAspectRatio="none"
            >
              <path
                fill="#16A34A"
                fillOpacity="0.5"
                stroke="#000000"
                strokeWidth="1"
                d="M 800 0 C 700 400, 100 400, 0 800 L 0 0 Z"
              />
            </svg>
          </div>
          {/* Logo placed on top of the curve */}
          <div className="flex justify-center p-4 sm:absolute sm:left-10 sm:top-4 sm:mt-0 sm:p-0">
            <img
              className="h-14 md:h-14 xl:h-16"
              src={talyko}
              alt="Talyko Logo"
            />
          </div>
        </div>

        {/* Other quadrants */}
        <div className="relative h-full w-full">
          <div className="absolute hidden h-full w-full md:block">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 800 800"
              className="h-full w-full"
              preserveAspectRatio="none"
            >
              <path
                fill="#A2D8D8"
                fillOpacity="0.5"
                stroke="#000000"
                strokeWidth="1"
                d="M 150 0 C 200 300, 600 300, 800 600 L 800 0 Z"
              />
            </svg>
          </div>
        </div>

        <div></div>

        <div className="relative h-full w-full">
          <div className="absolute hidden h-full w-full sm:block">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 800 800"
              className="h-full w-full"
              preserveAspectRatio="none"
            >
              <path
                fill="#F8CF2C"
                fillOpacity="0.5"
                stroke="#000000"
                strokeWidth="1"
                d="M 0 800 C 100 400, 700 400, 800 0 L 800 800 Z"
              />
            </svg>
          </div>
        </div>
      </Background>
      <form onSubmit={handleSubmit(handleCreateCompany)}>
        <Card className="absolute left-1/2 top-96 mx-auto h-[540px] w-[380px] -translate-x-1/2 -translate-y-1/2 space-y-7 rounded-[2.5rem] border border-black p-5 font-Hemmers text-black sm:top-1/2 md:w-[450px] xl:w-[500px]">
          <div className="absolute inset-0 h-10 w-full rounded-tl-full rounded-tr-full bg-[#F8CF2C] p-0">
            <div className="absolute inset-6 inset-x-5 flex items-center justify-end gap-4">
              <span className="flex size-3 items-center rounded-full bg-black" />
              <span className="flex size-3 items-center rounded-full bg-black" />
              <span className="flex size-3 items-center rounded-full bg-black" />
            </div>
          </div>
          <CardHeader className="flex items-center text-2xl md:text-3xl">
            Register Your Company
            <div></div>
            <Progress value={66} />
          </CardHeader>
          <CardContent>
            <div className="w-full space-y-4">
              <Input
                className="bg-[#ECECEC]"
                placeholder="Company Name"
                {...register("companyName")}
              />
              <Input
                className="bg-[#ECECEC]"
                placeholder="Company Email"
                {...register("companyEmail")}
              />
              <Input
                className="bg-[#ECECEC]"
                placeholder="Company Address"
                {...register("companyAddress")}
              />
              <div className="flex space-x-4">
                <Controller
                  name="companyState"
                  control={control}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger
                        className={`w-[80px] bg-[#ECECEC] ${
                          field.value ? "text-black" : "text-gray-500" // make placeholder gray
                        }`}
                      >
                        <SelectValue placeholder="State" />
                      </SelectTrigger>
                      <SelectContent className="w-[80px]">
                        {states.map((state) => (
                          <SelectItem key={state.value} value={state.label}>
                            {state.value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                <Input
                  className="bg-[#ECECEC]"
                  placeholder="City"
                  {...register("companyCity")}
                />
                <Input
                  className="w-[80px] bg-[#ECECEC]"
                  placeholder="Zip"
                  {...register("companyZip")}
                />
              </div>
            </div>
          </CardContent>
          <CardFooter>
            <Button
              className="w-full bg-blue-950 hover:bg-blue-900"
              type="submit"
            >
              Next
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
}
